import { type List, Map, Set } from "immutable";
import { useCallback, useRef, useState } from "react";

import { useOpenGlobalErrorModal } from "./useGlobalError";
import { useThrottleFnAsync } from "./useThrottleFnAsync";

type TBulkFetchOptions = { suppressError?: boolean; throttleTime?: number };
type TFetchingState = "Error" | "Initial" | "Loaded" | "Loading" | "Reloading";

const throttleOptions = { leading: false };

export const useThrottledBulkFetch = <T extends { id: string }, TOptions extends TBulkFetchOptions = TBulkFetchOptions>(
  fetchFn: (ids: string[], options?: TOptions) => Promise<List<T>>,
  options: TOptions = {} as TOptions,
) => {
  const openGlobalErrorModal = useOpenGlobalErrorModal();
  const { suppressError = false, throttleTime = 50 } = options;
  const [itemsById, setItemsById] = useState(Map<string, null | T>());
  const itemsState = useRef(Map<string, TFetchingState>());

  const fetchBulkData = useCallback(async () => {
    const initialIds = Set(itemsState.current.filter((state) => state === "Initial").keySeq());
    const reloadingIds = Set(itemsState.current.filter((state) => state === "Reloading").keySeq());
    const ids = initialIds.union(reloadingIds);
    if (!ids.size) return;
    try {
      itemsState.current = itemsState.current.merge(
        initialIds.map((id) => [id, "Loading"] as [string, TFetchingState]),
      );
      const newItems = await fetchFn(ids.toArray(), options);
      const newItemsIds = Set(newItems.map((item) => item.id).valueSeq());
      const missingIds = ids.filter((id) => !newItemsIds.has(id));
      itemsState.current = itemsState.current.merge(ids.map((id) => [id, "Loaded"]));
      setItemsById((curr) =>
        curr.merge(
          newItems.map((item) => [item.id, item]).concat(missingIds.map((id) => [id, null])) as List<[string, null | T]>,
        ),
      );
    } catch (error) {
      itemsState.current = itemsState.current.merge(ids.map((id) => [id, "Error"]));
      if (!suppressError) {
        openGlobalErrorModal(new Error("Failed to fetch bulk data: " + (error as Error).message));
      } else {
        // In order for the suppress to work here, suppressError must also be handled in the fetchFn itself (apiReq calls)
        throw error;
      }
    }
  }, [fetchFn, openGlobalErrorModal, options, suppressError]);

  const throttledFetchBulkData = useThrottleFnAsync(fetchBulkData, throttleTime, throttleOptions);

  const loadIds = useCallback(
    async (ids: string[], forceReload = false) => {
      const validIds = ids.filter((id) => !!id);
      const missingIds = Set(validIds.filter((id) => !itemsState.current.has(id)));
      const reloadIds = forceReload ? Set(validIds.filter((id) => itemsState.current.has(id))) : Set<string>();
      if (!missingIds.size && !reloadIds.size) return;
      itemsState.current = itemsState.current.merge(
        missingIds.map((id) => [id, "Initial"] as [string, TFetchingState]),
        reloadIds.map((id) => [id, "Reloading"] as [string, TFetchingState]),
      );
      await throttledFetchBulkData();
    },
    [throttledFetchBulkData],
  );

  return { itemsById, loadIds, setItemsById };
};
